import { useCallback, useMemo } from 'react';
import { getScheduleExecutionTime } from '@app/utils/getScheduleExecutionTime';
import getSendData from '@app/reports/getSendData';
import { getFetchPostPayload } from '@app/reports/fetchPosts';
import { sportsApiUrl, srsApiUrl } from '@app/utils/apiUrls';
import sportsApi from '@app/utils/sportsApi';
import fetchSRS from '@app/utils/fetchSRS';
import { useTranslation } from 'react-i18next';
import hasRequiredSelection, {
    MISSING_SELECTION_MESSAGE_KEY,
} from '../hasRequiredSelection';

interface UseScheduleReportProps {
    values: any;
    user: any;
    client: any;
    filters: any;
    sendNotification: (message: string, options?: any) => void;
    addScheduledReport: (schedule: any) => void;
    setActiveTable?: (table: string) => void;
    resetForm?: () => void;
    setSubmitting?: (submitting: boolean) => void;
    editScheduleId?: string | number | null;
    editScheduleData?: any;
    rightsholderAttribution?: string;
}

export const useScheduleReport = ({
    values,
    user,
    client,
    filters,
    sendNotification,
    addScheduledReport,
    setActiveTable,
    resetForm,
    setSubmitting,
    editScheduleId = null,
    editScheduleData = null,
    rightsholderAttribution = null,
}: UseScheduleReportProps) => {
    const { t } = useTranslation('tools');
    const scheduleStartDate = useMemo(() => {
        if (!values.startDate && !values.scheduleReportStartDate) return null;
        if (values.scheduleReportStartDate)
            return new Date(values.scheduleReportStartDate).toISOString();
        if (values.startDate) return new Date(values.startDate).toISOString();
    }, [values.startDate, values.scheduleReportStartDate]);

    const cleanValues = (inputValues: any) => {
        const v = JSON.parse(JSON.stringify(inputValues));
        if (Array.isArray(v.rightsholders)) {
            v.rhWithSP = [...v.rightsholders];
            v.rightsholders = v.rightsholders.reduce((acc, rhId) => {
                acc[rhId] = true;
                return acc;
            }, {});
        }
        return v;
    };

    const sendSchedule = useCallback(async () => {
        if (!editScheduleId && !hasRequiredSelection(values)) {
            sendNotification(t(MISSING_SELECTION_MESSAGE_KEY), {
                context: 'danger',
            });
            if (setSubmitting) setSubmitting(false);
            return false;
        }

        if (setSubmitting) setSubmitting(true);
        const { id: clientId } = client;

        const executionTime = getScheduleExecutionTime({
            startDate: values.scheduleReportStartDate || values.startDate,
            cadence: values.scheduleReportCadence,
            dayOfMonth: 10,
        });

        let payload;

        if (editScheduleId && editScheduleData) {
            // Edit mode: preserve original data and only update allowed fields
            const originalHttpRequestData = JSON.parse(
                editScheduleData.httpRequestData,
            );

            // Update only the startDate in the original httpRequestData
            const updatedHttpRequestPayload = {
                ...JSON.parse(originalHttpRequestData.requestPayload),
                data: {
                    ...JSON.parse(originalHttpRequestData.requestPayload).data,
                    startDate: scheduleStartDate,
                },
            };

            payload = {
                mvcReportName:
                    values.scheduleReportName || editScheduleData.mvcReportName,
                cadence: values.scheduleReportCadence,
                emailRecipients: [user.email],
                timezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
                postCoverPhotoHttpRequestData:
                    editScheduleData.postCoverPhotoHttpRequestData, // Preserve original
                httpRequestData: JSON.stringify({
                    ...originalHttpRequestData,
                    requestPayload: JSON.stringify(updatedHttpRequestPayload),
                }),
                clientId,
                nextExecutionAt: executionTime,
                creatorUserId: user.id,
            };
        } else {
            // Create new schedule: use current form values
            const cleaned = cleanValues(values);

            const sendData = getSendData({
                user,
                filters,
                client,
                values: cleaned,
                rightsholderAttribution,
            });

            const { action, domainObject } = getFetchPostPayload({
                ...cleaned,
                startDate: scheduleStartDate,
                client,
                rightsholderAttribution,
            });

            const postCoverPhotoHttpRequestPayload = {
                action,
                domainObject: {
                    ...domainObject,
                    client,
                },
            };

            const httpRequestPayload = {
                action: 'generateppt',
                data: {
                    ...sendData,
                    startDate: scheduleStartDate,
                },
            };

            payload = {
                mvcReportName:
                    cleaned.scheduleReportName || cleaned.reportTitle,
                cadence: cleaned.scheduleReportCadence,
                emailRecipients: [user.email],
                timezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
                postCoverPhotoHttpRequestData: JSON.stringify({
                    requestUrl: `${sportsApiUrl}/social-posts`,
                    requestMethod: 'POST',
                    requestHeaders: JSON.stringify({
                        'api-consumer-key': 'mvc',
                        'content-type': 'application/json',
                    }),
                    requestPayload: JSON.stringify(
                        postCoverPhotoHttpRequestPayload,
                    ),
                }),
                httpRequestData: JSON.stringify({
                    requestUrl: `${srsApiUrl}/reports`,
                    requestMethod: 'POST',
                    requestHeaders: JSON.stringify({
                        'api-consumer-key': 'mvc',
                        'content-type': 'application/json',
                    }),
                    requestPayload: JSON.stringify(httpRequestPayload),
                }),
                clientId,
                nextExecutionAt: executionTime,
                creatorUserId: user.id,
            };
        }

        try {
            let schedule;
            if (editScheduleId) {
                // Edit mode: update existing schedule
                schedule = await sportsApi
                    .url(
                        `/mvc-schedules/reports/${editScheduleId}?clientId=${client.id}`,
                    )
                    .json(payload)
                    .put()
                    .json();
            } else {
                // Create new schedule
                schedule = await sportsApi
                    .url(`/mvc-schedules/reports`)
                    .json(payload)
                    .post()
                    .json();
            }

            addScheduledReport(schedule);
            if (setActiveTable) setActiveTable('schedules');
            if (!editScheduleId && resetForm) {
                resetForm();
            }
            if (setSubmitting) setSubmitting(false);

            sendNotification(
                `Report schedule ${editScheduleId ? 'updated' : 'created'} successfully.`,
                { context: 'success' },
            );
            return true;
        } catch (error) {
            console.error('Error creating schedule:', error);
            sendNotification(
                'There was a problem creating your report schedule, please try again.',
                { context: 'danger' },
            );
            if (setSubmitting) setSubmitting(false);
            return false;
        }
    }, [
        values,
        user,
        client,
        filters,
        scheduleStartDate,
        addScheduledReport,
        setActiveTable,
        resetForm,
        setSubmitting,
        sendNotification,
        editScheduleId,
        editScheduleData,
        rightsholderAttribution,
        t,
    ]);

    const sendTest = useCallback(async () => {
        if (!editScheduleId && !hasRequiredSelection(values)) {
            sendNotification(t(MISSING_SELECTION_MESSAGE_KEY), {
                context: 'danger',
            });
            return false;
        }

        let data;

        if (editScheduleId && editScheduleData) {
            // Edit mode: use original data with updated startDate
            const originalHttpRequestData = JSON.parse(
                editScheduleData.httpRequestData,
            );
            const originalPayload = JSON.parse(
                originalHttpRequestData.requestPayload,
            );

            // Use original data but update startDate
            data = {
                ...originalPayload.data,
                startDate: scheduleStartDate,
                isTest: true,
                recipient: user.email,
                cadence: values.scheduleReportCadence,
                emailSubject:
                    values.scheduleReportName || editScheduleData.mvcReportName,
            };
        } else {
            // Create mode: use current form values
            const cleaned = cleanValues(values);

            const sendData = getSendData({
                user,
                filters,
                client,
                values: cleaned,
                rightsholderAttribution,
            });

            let endDate = new Date();
            if (cleaned.scheduleReportCadence === 'monthly') {
                endDate.setDate(0);
            } else if (cleaned.scheduleReportCadence === 'weekly') {
                endDate = new Date(
                    endDate.setDate(endDate.getDate() - endDate.getDay() + 6),
                );
            }

            data = {
                ...sendData,
                isTest: true,
                recipient: user.email,
                cadence: cleaned.scheduleReportCadence,
                emailSubject: cleaned.scheduleReportName,
                startDate: scheduleStartDate,
                endDate: endDate.toISOString(),
            };
        }

        try {
            await fetchSRS
                .url(`/reports`)
                .json({
                    action: 'generateppt',
                    data,
                })
                .post()
                .json();
            return true;
        } catch (error) {
            console.error('Error sending test:', error);
            sendNotification(
                'There was a problem sending the test email, please try again.',
                { context: 'danger' },
            );
            return false;
        }
    }, [
        values,
        user,
        filters,
        client,
        scheduleStartDate,
        sendNotification,
        editScheduleId,
        editScheduleData,
        rightsholderAttribution,
        t,
    ]);

    return {
        sendSchedule,
        sendTest,
        scheduleStartDate,
    };
};
